import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, convertToParamMap, ParamMap, Router } from '@angular/router';
import { forkJoin, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import {
  AuctionAttempt,
  AuctionHouseSummary,
  AuctionLotChange,
  AuctionLotDetailDto,
  AuctionLotDto,
  AuctionLotsQuery,
  AuctionLotsResponse,
  AuctionSale,
  AuctionTab,
  AuctionTabCounts,
} from 'src/app/models/auction-lot';
import { AccountV2Service } from 'src/app/services/account-v2-service';
import { AuctionLotsService } from 'src/app/services/auction-lots-service';
import { QuickSignupService } from 'src/app/services/quick-signup-service';
import { SessionService } from 'src/app/services/session-service';
import { environment } from 'src/environments/environment';
import {
  ago,
  dayLabel,
  fmtLeft,
  gbp,
  monthYear,
  regKey,
  styleLabel,
  timeOfDay,
} from './auction-format';

type PageTab = AuctionTab | 'following';
type Phase = 'soon' | 'live' | 'closing' | 'ended';

interface PageState {
  tab: PageTab;
  q: string;
  house: string;
  style: string;
  price: string;
  reserve: string;
  sort: string;
  dir: string; // '' = column's natural first-click direction | asc | desc
  view: 'grid' | 'list';
  page: number;
  /** One named sale by auction code ('' = every sale). */
  sale: string;
  /** '1' = admin has added Reg Transfers lots back in. */
  rt: string;
}

interface CtxRow {
  label: string;
  value: string;
  kind: '' | 'none' | 'hl';
}

/** View model: the DTO plus everything derived for display. */
export interface LotVm {
  dto: AuctionLotDto;
  phase: Phase;
  opensAt: number;
  closesAt: number;
  observedAt: number;
  leftText: string;
  agoText: string;
  pillText: string;
  pillClass: string;
  outcome: string;
  styleText: string;
  bidsText: string;
  reserveText: string;
  reserveClass: string;
  ctx: CtxRow[];
  flash: boolean;
  /** Price slot: label + text, phase-aware, never "£0" or blank. */
  priceLabel: string;
  priceText: string;
  hasPrice: boolean;
  allInText: string;
  /** Results line, e.g. "Sold at £x" / "Sold · final bid £x". */
  endedText: string;
  /** House plus the sale, e.g. "DVLA · November". */
  houseText: string;
}

interface SparkModel {
  path: string;
  dots: { x: number; y: number; r: number; fill: string }[];
  grid: { y: number; label: string }[];
  firstLabel: string;
  w: number;
  h: number;
  p: number;
}

const DEFAULTS: PageState = {
  tab: 'live',
  q: '',
  house: 'all',
  style: 'all',
  price: 'all',
  reserve: 'all',
  sort: 'ending',
  dir: '',
  view: 'grid',
  page: 1,
  sale: '',
  rt: '',
};
const PAGE_SIZE = 48;
const POLL_MS = 30000;
const VIEW_KEY = 'pxa-view';
const TABS: PageTab[] = ['live', 'today', 'soon', 'results', 'following'];

/**
 * Live Auctions (dev2/dev only — gated in app.module). Every live number plate
 * auction lot in one place: tabs, filters (all mirrored in the query string),
 * equal-slot cards or dense rows, a right-hand lot drawer and a fees explainer.
 * Implements the approved prototype docs/platex-weekend-redesign/
 * live-auctions-prototype.html against /api/Auction/lots*.
 *
 * OnPush + a 1 s countdown tick that runs outside Angular and only re-checks
 * this view, so the ticking clocks never trigger app-wide change detection.
 */
@Component({
  selector: 'pla-live-auctions',
  templateUrl: './live-auctions.component.html',
  styleUrls: ['./live-auctions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LiveAuctionsComponent implements OnInit, OnDestroy {
  public readonly pageSize = PAGE_SIZE;

  public state: PageState = { ...DEFAULTS };
  public qInput = '';
  public now = Date.now();

  public loading = true;
  public error = false;
  public lots: LotVm[] = [];
  public total = 0;
  public counts: AuctionTabCounts | null = null;
  public houses: AuctionHouseSummary[] = [];
  public liveBidTotalPence = 0;
  /** Named sales (sale cards); kept from the last response that carried them. */
  public sales: AuctionSale[] = [];
  /** Name of the ?sale= auction from the API (also for sales that are over). */
  public saleTitle: string | null = null;

  public followedKeys = new Set<string>();
  public followingCount: number | null = null;

  public drawer: LotVm | null = null;
  public detail: AuctionLotDetailDto | null = null;
  public detailLoading = false;
  public spark: SparkModel | null = null;

  public readonly tabs: { key: PageTab; label: string }[] = [
    { key: 'live', label: 'Live' },
    { key: 'today', label: 'Closing today' },
    { key: 'soon', label: 'Coming soon' },
    { key: 'results', label: 'Results' },
    { key: 'following', label: 'Following' },
  ];
  public readonly priceBands = [
    { key: 'all', label: 'Any' },
    { key: '0-1000', label: 'Under £1,000' },
    { key: '1000-5000', label: '£1,000 – £5,000' },
    { key: '5000-20000', label: '£5,000 – £20,000' },
    { key: '20000-', label: '£20,000+' },
  ];

  public gbp = gbp;
  public timeOfDay = timeOfDay;

  private since: string | null = null;
  private lastPoll = 0;
  private followsLoaded = false;
  private followBusy = new Set<string>();
  private reloadScheduled = false;
  private q$ = new Subject<string>();
  private subs: Subscription[] = [];
  private listSub?: Subscription;
  private tickHandle: any;
  private pollHandle: any;
  private estHandle: any;

  /** Locked-in PlateX estimates per lot ("house:id"), admin-only (platex-assistant). */
  public estimates: { [lotKey: string]: LotEstimate } = {};
  public estScore: EstScore | null = null;
  /** Lot keys ("house:id") of every finished lot in the filtered sale, for a per-sale scorecard. */
  private saleResultKeys: { sale: string; keys: string[] } | null = null;
  private saleKeysSub?: Subscription;
  private saleScoreMemo: { keys: object; estimates: object; score: EstScore['overall'] } | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private service: AuctionLotsService,
    private accountService: AccountV2Service,
    private signupService: QuickSignupService,
    private sessionService: SessionService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private host: ElementRef<HTMLElement>
  ) {}

  // ---------------------------------------------------------------- lifecycle
  ngOnInit(): void {
    this.subs.push(this.route.queryParamMap.subscribe((p) => this.onParams(p)));
    this.subs.push(
      this.q$.pipe(debounceTime(300), distinctUntilChanged()).subscribe((q) => this.setQuery({ q, page: 1 }))
    );
    this.loadFollows();
    this.loadEstimates();
    this.zone.runOutsideAngular(() => {
      this.estHandle = setInterval(() => this.zone.run(() => this.loadEstimates()), 5 * 60 * 1000);
      this.tickHandle = setInterval(() => this.tick(), 1000);
      this.pollHandle = setInterval(() => this.poll(), POLL_MS);
      window.addEventListener('scroll', this.syncStickyTop, { passive: true });
      window.addEventListener('resize', this.syncStickyTop, { passive: true });
    });
    setTimeout(this.syncStickyTop);
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.listSub?.unsubscribe();
    this.saleKeysSub?.unsubscribe();
    clearInterval(this.tickHandle);
    clearInterval(this.pollHandle);
    clearInterval(this.estHandle);
    window.removeEventListener('scroll', this.syncStickyTop);
    window.removeEventListener('resize', this.syncStickyTop);
    this.unlockBody();
  }

  /**
   * Sticky tabs/toolbar offset = wherever the site header currently ends (the
   * dev2 nav shell scrolls away; the legacy header is fixed), so the bar
   * docks flush under a visible header and at 0 once it has gone.
   */
  private syncStickyTop = (): void => {
    const header = document.querySelector('pla-nav-shell .ns, .site-header-container.fixed .site-header');
    const bottom = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    this.host.nativeElement.style.setProperty('--la-top', bottom + 'px');
  };

  @HostListener('document:visibilitychange')
  onVisibility(): void {
    if (document.visibilityState === 'visible' && Date.now() - this.lastPoll > POLL_MS) this.poll();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.drawer) this.closeLot();
  }

  // ----------------------------------------------------------- URL <-> state
  private onParams(p: ParamMap): void {
    const tab = p.get('tab') as PageTab;
    let view = p.get('view') as 'grid' | 'list' | null;
    if (view !== 'grid' && view !== 'list') {
      view = this.storedView();
    }
    const next: PageState = {
      tab: TABS.includes(tab) ? tab : DEFAULTS.tab,
      q: p.get('q') || '',
      house: p.get('house') || DEFAULTS.house,
      style: p.get('style') || DEFAULTS.style,
      price: p.get('price') || DEFAULTS.price,
      reserve: p.get('reserve') || DEFAULTS.reserve,
      sort: p.get('sort') || DEFAULTS.sort,
      dir: p.get('dir') === 'asc' || p.get('dir') === 'desc' ? (p.get('dir') as string) : '',
      view,
      page: Math.max(1, parseInt(p.get('page') || '1', 10) || 1),
      sale: (p.get('sale') || '').trim().toUpperCase(),
      rt: p.get('rt') === '1' && this.isAdmin ? '1' : '',
    };
    const prev = this.state;
    this.state = next;
    if (this.qInput.replace(/\s/g, '').toUpperCase() !== next.q) this.qInput = next.q;
    const dataKeys: (keyof PageState)[] = ['tab', 'q', 'house', 'style', 'price', 'reserve', 'sort', 'dir', 'page', 'sale', 'rt'];
    const changed = !this.lots.length && this.loading ? true : dataKeys.some((k) => prev[k] !== next[k]);
    if (changed || !this.counts) this.load();
    this.cdr.markForCheck();
  }

  /**
   * Merge a patch into the query string (defaults omitted; mock flag kept).
   * Rewrites the address bar in place instead of navigating: the app shell
   * never reuses a route and scrolls to the top after every navigation, which
   * rebuilt this page and threw the reader back to the top on each sort.
   */
  public setQuery(patch: Partial<PageState>): void {
    const s = { ...this.state, ...patch };
    const qp: { [k: string]: any } = {};
    (Object.keys(DEFAULTS) as (keyof PageState)[]).forEach((k) => {
      if (k === 'view') return;
      if (s[k] !== DEFAULTS[k]) qp[k] = s[k];
    });
    if (s.view !== DEFAULTS.view) qp['view'] = s.view;
    if (this.service.mock) qp['mock'] = 1;
    const url = this.router.serializeUrl(this.router.createUrlTree([], { relativeTo: this.route, queryParams: qp }));
    this.location.replaceState(url);
    this.onParams(convertToParamMap(qp));
  }

  public onSearchInput(v: string): void {
    this.qInput = v;
    this.q$.next((v || '').replace(/\s/g, '').toUpperCase());
  }

  public setTab(tab: PageTab): void {
    // Coming soon only sorts by opening time (prototype behaviour).
    this.setQuery({ tab, page: 1, sort: tab === 'soon' ? 'ending' : this.state.sort, dir: tab === 'soon' ? '' : this.state.dir });
  }

  public setView(view: 'grid' | 'list'): void {
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch (e) {}
    this.setQuery({ view });
  }

  // ------------------------------------------------------------ column sort
  // First click uses the column's natural direction (text A→Z, money/bids
  // high→low, ending soonest first); clicking the active column flips it.
  private static readonly COL_NATURAL: { [k: string]: 'asc' | 'desc' } = {
    registration: 'asc', house: 'asc', status: 'asc', reserve: 'asc', ending: 'asc',
    price: 'desc', allin: 'desc', bids: 'desc', lastsold: 'desc',
  };

  /** Map legacy select values onto their column + direction. */
  private get activeCol(): { col: string; dir: 'asc' | 'desc' } {
    const s = this.state.sort;
    if (s === 'high') return { col: 'price', dir: 'desc' };
    if (s === 'low') return { col: 'price', dir: 'asc' };
    const nat = LiveAuctionsComponent.COL_NATURAL[s] || 'asc';
    return { col: s, dir: (this.state.dir as 'asc' | 'desc') || nat };
  }

  public sortBy(col: string): void {
    const nat = LiveAuctionsComponent.COL_NATURAL[col] || 'asc';
    const cur = this.activeCol;
    const dir = cur.col === col ? (cur.dir === 'asc' ? 'desc' : 'asc') : nat;
    this.setQuery({ sort: col, dir: dir === nat ? '' : dir, page: 1 });
  }

  public sortState(col: string): 'ascending' | 'descending' | 'none' {
    const cur = this.activeCol;
    return cur.col !== col ? 'none' : cur.dir === 'asc' ? 'ascending' : 'descending';
  }

  public onSortSelect(v: string): void {
    this.setQuery({ sort: v, dir: '', page: 1 });
  }

  private storedView(): 'grid' | 'list' {
    try {
      const v = localStorage.getItem(VIEW_KEY);
      if (v === 'grid' || v === 'list') return v;
    } catch (e) {}
    return 'grid';
  }

  // ---------------------------------------------------------------- loading
  private buildQuery(tab: AuctionTab, page: number, pageSize: number): AuctionLotsQuery {
    const s = this.state;
    let minBid: number | null = null;
    let maxBid: number | null = null;
    if (s.price !== 'all') {
      const [a, b] = s.price.split('-');
      minBid = a ? Number(a) : null;
      maxBid = b ? Number(b) : null;
    }
    return {
      q: s.q,
      house: s.house,
      tab,
      style: s.style,
      minBid,
      maxBid,
      reserve: s.reserve,
      sort: s.sort,
      dir: s.dir,
      page,
      pageSize,
      sale: s.sale || undefined,
      rt: s.rt === '1' ? true : undefined,
    };
  }

  public load(): void {
    this.listSub?.unsubscribe();
    this.loading = true;
    this.error = false;
    this.cdr.markForCheck();
    if (this.state.tab === 'following') {
      this.listSub = this.fetchFollowed().subscribe(
        (r) => {
          if (!r) return this.fail();
          this.applyMeta(r.meta);
          this.lots = r.items.map((d) => this.toVm(d));
          this.total = this.lots.length;
          this.followingCount = this.total;
          this.done();
        },
        () => this.fail()
      );
      return;
    }
    this.listSub = this.service
      .getLots({ ...this.buildQuery(this.state.tab, this.state.page, PAGE_SIZE), includeSales: true })
      .subscribe(
        (r) => {
          this.applyMeta(r);
          if (r.sales) this.sales = r.sales;
          this.saleTitle = r.saleTitle || null;
          this.lots = (r.items || []).map((d) => this.toVm(d));
          this.total = r.total || 0;
          this.loadSaleResultKeys();
          this.done();
        },
        () => this.fail()
      );
  }

  private applyMeta(r: AuctionLotsResponse): void {
    this.counts = r.counts;
    this.houses = r.houses || [];
    this.liveBidTotalPence = r.liveBidTotalPence || 0;
    this.since = r.generatedAt || new Date().toISOString();
  }

  private done(): void {
    this.loading = false;
    this.now = Date.now();
    this.lastPoll = Date.now();
    if (this.drawer) {
      const fresh = this.lots.find((l) => l.dto.id === this.drawer!.dto.id);
      if (fresh) this.drawer = fresh;
    }
    this.cdr.markForCheck();
  }

  private fail(): void {
    this.loading = false;
    this.error = true;
    this.lots = [];
    this.total = 0;
    this.cdr.markForCheck();
  }

  /**
   * Following = lots (live, upcoming, finished) whose registration the user
   * follows. The API has no follow filter, so union the three tabs and filter
   * client-side.
   */
  private fetchFollowed() {
    const tabs: AuctionTab[] = ['live', 'soon', 'results'];
    return forkJoin(tabs.map((t) => this.service.getLots({ ...this.buildQuery(t, 1, 200), sort: 'ending' }))).pipe(
      map((rs: AuctionLotsResponse[]) => {
        const seen = new Set<string>();
        const items: AuctionLotDto[] = [];
        rs.forEach((r) =>
          (r.items || []).forEach((d) => {
            if (seen.has(d.id) || !this.followedKeys.has(regKey(d.registration))) return;
            seen.add(d.id);
            items.push(d);
          })
        );
        return { meta: rs[0], items } as { meta: AuctionLotsResponse; items: AuctionLotDto[] } | null;
      }),
      catchError(() => of(null))
    );
  }

  /** Background count for the Following tab badge. */
  private refreshFollowingCount(): void {
    if (!this.followedKeys.size) {
      this.followingCount = 0;
      this.cdr.markForCheck();
      return;
    }
    this.fetchFollowed().subscribe((r) => {
      if (r) this.followingCount = r.items.length;
      this.cdr.markForCheck();
    });
  }

  // ---------------------------------------------------------------- refresh
  private poll(): void {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    if (this.loading || !this.since || !this.lots.length) return;
    this.lastPoll = Date.now();
    this.service.getChanges(this.since).subscribe(
      (changes) => this.zone.run(() => this.applyChanges(changes || [])),
      () => {}
    );
  }

  private applyChanges(changes: AuctionLotChange[]): void {
    if (!changes.length) return;
    let latest = this.since ? Date.parse(this.since) : 0;
    const byId = new Map(this.lots.map((l) => [l.dto.id, l]));
    changes.forEach((c) => {
      const t = c.observedAt ? Date.parse(c.observedAt) : 0;
      if (t > latest) latest = t;
      const vm = byId.get(c.id);
      if (!vm) return;
      const bidMoved = c.currentBidPence !== vm.dto.currentBidPence;
      const dto: AuctionLotDto = {
        ...vm.dto,
        currentBidPence: c.currentBidPence,
        bidCount: c.bidCount,
        reserveStatus: c.reserveStatus,
        biddingClosesAt: c.biddingClosesAt,
        observedAt: c.observedAt,
      };
      if (bidMoved) dto.allInPence = this.estimateAllIn(vm.dto, c.currentBidPence);
      const next = this.toVm(dto);
      next.flash = bidMoved;
      this.lots = this.lots.map((l) => (l === vm ? next : l));
      if (this.drawer && this.drawer.dto.id === c.id) this.drawer = next;
      if (bidMoved) setTimeout(() => ((next.flash = false), this.cdr.markForCheck()), 1700);
    });
    if (latest) this.since = new Date(latest).toISOString();
    this.cdr.markForCheck();
  }

  /** Keeps the all-in figure in step with a polled bid by scaling the fee share. */
  private estimateAllIn(old: AuctionLotDto, bid: number | null): number | null {
    if (bid == null || old.allInPence == null) return old.allInPence;
    const prevBid = old.currentBidPence || 0;
    if (old.house === 'regtransfers') {
      const fixed = old.allInPence - prevBid * (old.noVat ? 1 : 1.2);
      return Math.round(bid * (old.noVat ? 1 : 1.2) + fixed);
    }
    if (old.house === 'collecting_cars') return Math.round(bid + Math.max(bid * 0.12, 30000));
    return old.allInPence - prevBid + bid;
  }

  /** 1 s tick, outside Angular: countdowns, "N min ago", closing/ended flips. */
  private tick(): void {
    this.now = Date.now();
    let ended = false;
    const touch = (vm: LotVm) => {
      const before = vm.phase;
      this.decorateTime(vm);
      if (before !== 'ended' && vm.phase === 'ended') ended = true;
    };
    this.lots.forEach(touch);
    if (this.drawer && !this.lots.includes(this.drawer)) touch(this.drawer);
    if (ended) this.scheduleReload();
    this.cdr.detectChanges();
  }

  private scheduleReload(): void {
    if (this.reloadScheduled) return;
    this.reloadScheduled = true;
    setTimeout(() => {
      this.reloadScheduled = false;
      this.zone.run(() => this.load());
    }, 5000);
  }

  // --------------------------------------------------------------- view model
  private toVm(dto: AuctionLotDto): LotVm {
    const vm = {
      dto,
      opensAt: dto.biddingOpensAt ? Date.parse(dto.biddingOpensAt) : 0,
      closesAt: dto.biddingClosesAt ? Date.parse(dto.biddingClosesAt) : 0,
      observedAt: dto.observedAt ? Date.parse(dto.observedAt) : 0,
      styleText: styleLabel(dto.style),
      flash: false,
      houseText: dto.houseName + (dto.saleLabel ? ' · ' + dto.saleLabel : ''),
    } as LotVm;
    vm.outcome = this.outcomeOf(dto);
    vm.bidsText = this.bidsTextOf(dto);
    const r = dto.reserveStatus;
    vm.reserveClass = r === 'met' ? 'res-met' : r === 'not_met' ? 'res-not' : 'res-unk';
    vm.reserveText =
      r === 'met'
        ? 'Reserve met'
        : r === 'not_met'
        ? 'Reserve not met'
        : r === 'no_reserve'
        ? 'No reserve'
        : 'Reserve not published';
    if (dto.reserveLowered && (r === 'met' || r === 'not_met')) vm.reserveText += ' (lowered)';
    vm.ctx = this.ctxRows(dto);
    this.decorateTime(vm);
    return vm;
  }

  private decorateTime(vm: LotVm): void {
    const now = this.now;
    const d = vm.dto;
    let phase: Phase;
    if (d.status === 'ended') phase = 'ended';
    else if (d.status === 'soon' && (!vm.opensAt || now < vm.opensAt)) phase = 'soon';
    else if (vm.closesAt && now >= vm.closesAt) phase = 'ended';
    else if (vm.closesAt && vm.closesAt - now < 36e5) phase = 'closing';
    else phase = 'live';
    vm.phase = phase;
    vm.leftText = vm.closesAt ? fmtLeft(vm.closesAt - now) : '';
    this.decoratePrice(vm);
    vm.agoText = ago(vm.observedAt, now);
    if (phase === 'soon') {
      vm.pillText = 'Opens ' + (vm.opensAt ? dayLabel(vm.opensAt, now).toLowerCase() : 'soon');
      vm.pillClass = 's-soon';
    } else if (phase === 'ended') {
      vm.pillText = vm.outcome;
      vm.pillClass = vm.outcome === 'Sold' ? 's-sold' : 's-ended';
    } else if (phase === 'closing') {
      vm.pillText = 'Closing';
      vm.pillClass = 's-closing';
    } else {
      vm.pillText = 'Live';
      vm.pillClass = 's-live';
    }
  }

  private bidsTextOf(d: AuctionLotDto): string {
    if (d.bidCount == null) return 'Bids shown at close';
    if (d.bidCount === 0) return 'No bids yet';
    const plus = d.bidCountLowerBound ? '+' : '';
    return d.bidCount + plus + ' bid' + (d.bidCount === 1 && !plus ? '' : 's');
  }

  /**
   * Price slot. No bids => currentBidPence is null and the house's start
   * price (startingPricePence) is shown as "Starting price"; all-in only when
   * the API supplied allInPence. Ended lots show the hammer, labelled
   * "Final bid" when PlateX inferred it from the last bid it saw.
   */
  private decoratePrice(vm: LotVm): void {
    const d = vm.dto;
    const start = d.startingPricePence ?? null;
    let label: string;
    let pence: number | null;
    if (vm.phase === 'ended') {
      pence = d.hammerPence ?? d.currentBidPence ?? null;
      label = pence == null ? 'No bids' : d.hammerPence != null && !d.hammerInferred ? 'Hammer price' : 'Final bid';
    } else if (d.currentBidPence != null) {
      pence = d.currentBidPence;
      label = 'Current bid';
    } else if (start != null) {
      pence = start;
      label = 'Starting price';
    } else {
      pence = null;
      label = vm.phase === 'soon' ? 'Starting soon' : 'No bids yet';
    }
    vm.priceLabel = label;
    vm.hasPrice = pence != null;
    vm.priceText = pence != null ? gbp(pence) : '—';
    vm.allInText = d.allInPence != null ? '≈ ' + gbp(d.allInPence) : '—';
    vm.endedText = this.endedTextOf(vm, pence);
  }

  private endedTextOf(vm: LotVm, pence: number | null): string {
    const d = vm.dto;
    const price = pence != null ? gbp(pence) : null;
    if (vm.outcome === 'Sold') {
      if (!price) return 'Sold';
      return d.hammerInferred || d.resultInferred ? 'Sold · final bid ' + price : 'Sold at ' + price;
    }
    if (vm.outcome === 'Not sold') {
      if (d.resultInferred) return 'Not sold (no sale reported)';
      return price ? 'Not sold · final bid ' + price : 'Not sold';
    }
    return price ? 'Closed at ' + price : 'Closed · no bids';
  }

  private outcomeOf(d: AuctionLotDto): string {
    const r = (d.resultStatus || '').toLowerCase();
    if (r === 'sold') return 'Sold';
    if (r === 'not_sold' || r === 'unsold' || r === 'withdrawn' || r === 'passed') return 'Not sold';
    return 'Result pending';
  }

  // Only facts we actually have: no "No sale on record" / "Not listed by dealers" filler.
  private ctxRows(d: AuctionLotDto): CtxRow[] {
    const rows: CtxRow[] = [];
    const att = d.previousAttempts;
    if (att && att.count > 0) {
      rows.push({
        label: 'Earlier attempts',
        value: att.count + '×' + (att.bestClosingBidPence ? ' · best ' + gbp(att.bestClosingBidPence) : ''),
        kind: 'hl',
      });
    }
    if (d.lastSold) {
      rows.push({ label: 'Last sold', value: gbp(d.lastSold.pricePence) + ' · ' + monthYear(d.lastSold.date), kind: '' });
    }
    const dealer = this.dealerRow(d);
    if (dealer) rows.push(dealer);
    return rows.slice(0, 2);
  }

  // A plate in auction is normally withdrawn from dealer sites, so a dealer price is what
  // it was previously offered at, not something you can buy now. Buy Now on the auction
  // house itself is current.
  private dealerRow(d: AuctionLotDto): CtxRow | null {
    if (d.dealerBest) {
      const p = d.dealerBest.pricePence == null ? 'POA' : gbp(d.dealerBest.pricePence);
      return { label: 'Previously offered at', value: p + ' · ' + d.dealerBest.dealer, kind: '' };
    }
    if (d.buyNowAvailable) {
      return {
        label: 'Buy Now',
        value: d.houseName + (d.buyNowPence ? ' · ' + gbp(d.buyNowPence) : ''),
        kind: '',
      };
    }
    return null;
  }

  // ------------------------------------------------------------ template helpers
  public trackLot(_: number, l: LotVm): string {
    return l.dto.id;
  }

  public tabCount(key: PageTab): number | null {
    if (key === 'following') return this.followingCount;
    return this.counts ? (this.counts as any)[key] : null;
  }

  /** Reg Transfers is never a house chip: admins add it back with the toggle. */
  public get trackedHouses(): AuctionHouseSummary[] {
    return this.houses.filter((h) => h.tracked && h.key !== 'regtransfers');
  }

  // ------------------------------------------------------------ sales + admin
  public get isAdmin(): boolean {
    return this.sessionService.isAdmin();
  }

  /** Admin-only: add Reg Transfers lots back into the results (off by default). */
  public toggleRegTransfers(): void {
    const on = this.state.rt !== '1';
    this.setQuery({ rt: on ? '1' : '', house: !on && this.state.house === 'regtransfers' ? 'all' : this.state.house, page: 1 });
  }

  /** Sale cards for this tab: upcoming sales on Coming soon, open ones on Live / Closing today. */
  public get tabSales(): AuctionSale[] {
    if (this.state.sale) return [];
    const t = this.state.tab;
    const house = this.state.house;
    const ofHouse = (s: AuctionSale) => house === 'all' || s.house === house;
    if (t === 'soon') return this.sales.filter((s) => s.status === 'soon' && ofHouse(s));
    if (t === 'live' || t === 'today') return this.sales.filter((s) => s.status === 'live' && ofHouse(s));
    return [];
  }

  public get activeSale(): AuctionSale | null {
    return this.state.sale ? this.sales.find((s) => s.code.toUpperCase() === this.state.sale) || null : null;
  }

  public selectSale(s: AuctionSale): void {
    this.setQuery({ sale: s.code, tab: s.status === 'live' ? 'live' : 'soon', house: 'all', page: 1 });
  }

  public clearSale(): void {
    this.setQuery({ sale: '', page: 1 });
  }

  /** "Opens Wed 18 Nov, 10:00" / "Lots close from Tue 24 Nov, 11:00". */
  public saleWhen(s: AuctionSale): string {
    const fmt = (iso: string) =>
      new Date(iso).toLocaleString('en-GB', {
        timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
      });
    if (s.status === 'soon' && s.opensAt) return 'Bidding opens ' + fmt(s.opensAt);
    if (s.firstLotClosesAt) return 'Lots close from ' + fmt(s.firstLotClosesAt);
    return '';
  }

  /** Live countdown to the sale's next moment: opening, else its first close. */
  public saleCountdown(s: AuctionSale): string {
    const target = s.status === 'soon' ? s.opensAt : s.firstLotClosesAt;
    if (!target) return '';
    const ms = Date.parse(target) - this.now;
    if (ms <= 0) return s.status === 'soon' ? 'Opening now' : 'Closing now';
    const d = Math.floor(ms / 864e5);
    const h = Math.floor((ms % 864e5) / 36e5);
    const m = Math.floor((ms % 36e5) / 6e4);
    const left = d > 0 ? `${d} day${d === 1 ? '' : 's'}, ${h} hour${h === 1 ? '' : 's'}` : `${h}h ${String(m).padStart(2, '0')}m`;
    return (s.status === 'soon' ? 'Opens in ' : 'First lots close in ') + left;
  }

  public saleSiteName(s: AuctionSale): string {
    if (!s.officialUrl) return s.houseName;
    try {
      return new URL(s.officialUrl).hostname.replace(/^www\./, '');
    } catch (e) {
      return s.houseName;
    }
  }

  public trackSale(_: number, s: AuctionSale): string {
    return s.code;
  }

  /** Chip counts: per-house live counts on the Live tab; total for "All" elsewhere. */
  public houseChipCount(key: string): number | null {
    if (key === 'all') {
      if (this.state.tab === 'following') return this.followingCount;
      return this.counts ? (this.counts as any)[this.state.tab] : null;
    }
    if (this.state.tab !== 'live') return null;
    const h = this.houses.find((x) => x.key === key);
    return h ? h.live : null;
  }

  public houseAgo(h: AuctionHouseSummary): string {
    return ago(h.lastCheckedAt ? Date.parse(h.lastCheckedAt) : null, this.now);
  }

  public get countLabel(): string {
    const lots = this.total === 1 ? 'lot' : 'lots';
    return (
      {
        live: 'live ' + lots,
        today: lots + ' closing today',
        soon: lots + ' opening soon',
        results: 'finished ' + lots,
        following: 'followed ' + lots,
      } as any
    )[this.state.tab];
  }

  public get emptyCopy(): { title: string; body: string } {
    if (this.state.tab === 'results' && !this.hasFilters) {
      return {
        title: 'Results appear after each close',
        body:
          'Reg Transfers lots close at 19:30 each evening and Collecting Cars lots a few minutes apart before that. Finished lots move here as they close.',
      };
    }
    if (this.state.tab === 'following' && !this.hasFilters) {
      return {
        title: 'You are not following any lots yet',
        body:
          'Tap the bell on any lot to get a notification when it is about to close, when the price moves, and when the result is in.',
      };
    }
    return { title: 'No lots match these filters', body: 'Try clearing the search or choosing All houses.' };
  }

  public get hasFilters(): boolean {
    const s = this.state;
    return !!s.q || s.house !== 'all' || s.style !== 'all' || s.price !== 'all' || s.reserve !== 'all' || !!s.sale;
  }

  public get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / PAGE_SIZE));
  }

  public get pagedByApi(): boolean {
    return this.state.tab !== 'following' && this.total > PAGE_SIZE;
  }

  public goPage(page: number): void {
    this.setQuery({ page });
    if (typeof window !== 'undefined') window.scrollTo({ top: 0 });
  }

  public clockDay(vm: LotVm): string {
    return dayLabel(vm.phase === 'soon' ? vm.opensAt : vm.closesAt, this.now);
  }

  public endedSub(vm: LotVm): string {
    if (vm.outcome === 'Not sold' && vm.dto.resultInferred && vm.hasPrice) {
      return 'Final bid ' + vm.priceText + ' · ' + dayLabel(vm.closesAt, this.now) + ' ' + timeOfDay(vm.closesAt);
    }
    if (vm.outcome === 'Result pending') {
      return vm.dto.house === 'regtransfers'
        ? "Confirmed from Reg Transfers' sold list next morning"
        : 'Result confirmed after the close';
    }
    return dayLabel(vm.closesAt, this.now) + ' ' + timeOfDay(vm.closesAt);
  }

  public isFollowed(vm: LotVm): boolean {
    return this.followedKeys.has(regKey(vm.dto.registration));
  }

  public plateDetailLink(vm: LotVm): string {
    return '/plates/' + regKey(vm.dto.registration);
  }

  // ------------------------------------------------------------------ follow
  private loadFollows(): void {
    if (this.service.mock) {
      this.followsLoaded = true;
      this.followingCount = 0;
      return;
    }
    if (!this.sessionService.isLoggedIn()) {
      this.followingCount = 0;
      return;
    }
    this.loadFollowsThen(() => this.refreshFollowingCount());
  }

  private loadFollowsThen(next: () => void): void {
    this.accountService.getFollowed().subscribe(
      (follows) => {
        this.followedKeys = new Set((follows || []).map((f) => regKey(f.registration)));
        this.followsLoaded = true;
        this.cdr.markForCheck();
        next();
      },
      () => {
        this.followsLoaded = true;
        next();
      }
    );
  }

  /**
   * Bell = follow the registration via the account follow API (intent
   * "tracking", price + sale-result alerts). Logged-out users get the site's
   * quick sign-up / login prompt first. In ?mock=1 mode it's a local toggle.
   */
  public toggleFollow(vm: LotVm, ev?: Event): void {
    ev?.stopPropagation();
    const key = regKey(vm.dto.registration);
    if (this.service.mock) {
      this.flipLocal(key, !this.followedKeys.has(key));
      return;
    }
    this.signupService.quickSignup(
      `follow ${vm.dto.registration}`,
      undefined,
      (ok: boolean) => {
        if (!ok) return;
        if (!this.followsLoaded) this.loadFollowsThen(() => this.doToggleFollow(key));
        else this.doToggleFollow(key);
      },
      () => {}
    );
  }

  private doToggleFollow(key: string): void {
    if (this.followBusy.has(key)) return;
    this.followBusy.add(key);
    const release = () => {
      this.followBusy.delete(key);
      this.cdr.markForCheck();
    };
    if (this.followedKeys.has(key)) {
      this.accountService.unfollow(key).subscribe(() => {
        this.flipLocal(key, false);
        release();
      }, release);
      return;
    }
    this.accountService.follow(key, 'tracking').subscribe(() => {
      this.flipLocal(key, true);
      this.accountService.updateFollowAlerts(key, ['price', 'sale_result'], 'instant').subscribe(release, release);
    }, release);
  }

  private flipLocal(key: string, on: boolean): void {
    const next = new Set(this.followedKeys);
    if (on) next.add(key);
    else next.delete(key);
    this.followedKeys = next;
    const inLots = this.lots.some((l) => regKey(l.dto.registration) === key);
    if (this.followingCount != null && (inLots || this.drawer)) {
      this.followingCount = Math.max(0, this.followingCount + (on ? 1 : -1));
    }
    if (this.state.tab === 'following' && !on) {
      this.lots = this.lots.filter((l) => regKey(l.dto.registration) !== key);
      this.total = this.lots.length;
    }
    this.cdr.markForCheck();
  }

  // ------------------------------------------------------------------ drawer
  public openLot(vm: LotVm): void {
    this.drawer = vm;
    this.detail = null;
    this.spark = null;
    this.detailLoading = true;
    this.lockBody();
    this.service.getLot(vm.dto.id).subscribe(
      (d) => {
        if (!this.drawer || this.drawer.dto.id !== d.id) return;
        this.detail = d;
        this.spark = this.buildSpark(d.attempts || [], vm.phase === 'soon' ? null : d.currentBidPence);
        this.detailLoading = false;
        this.cdr.markForCheck();
      },
      () => {
        this.detailLoading = false;
        this.cdr.markForCheck();
      }
    );
    setTimeout(() => {
      const x = document.querySelector('.la-drawer .la-x') as HTMLElement | null;
      x?.focus();
    });
  }

  public closeLot(): void {
    this.drawer = null;
    this.detail = null;
    this.spark = null;
    this.unlockBody();
    this.cdr.markForCheck();
  }

  public onCardKey(ev: KeyboardEvent, vm: LotVm): void {
    if (ev.key === 'Enter' && ev.target === ev.currentTarget) this.openLot(vm);
  }

  public stop(ev: Event): void {
    ev.stopPropagation();
  }

  // The backend's breakdown ends with its own "Estimated all-in" total row;
  // show only the components and take the total from allInPence.
  private isTotalRow(label: string): boolean {
    return /all-in|total/i.test(label || '');
  }

  public get feeRows(): { label: string; pence: number; note: string | null }[] {
    return (this.detail?.feeBreakdown || [])
      .filter((f) => !this.isTotalRow(f.label))
      .map((f) => ({
        label: f.label === 'Bid' ? 'Current bid' : f.label,
        pence: f.pence,
        // Drop internal basis codes and the hammer disclaimer; keep plain notes like "10% + 20% VAT".
        note: f.note && !/fees not included|_v\d{4}/i.test(f.note) ? f.note : null,
      }));
  }

  public get feeTotal(): number {
    const d = this.detail;
    if (!d) return 0;
    if (d.allInPence != null) return d.allInPence;
    const total = (d.feeBreakdown || []).find((f) => this.isTotalRow(f.label));
    return total ? total.pence : this.feeRows.reduce((s, f) => s + (f.pence || 0), 0);
  }

  public get attemptsSummary(): { count: number; since: string; best: number; bestAt: string; sold: number } | null {
    const a = this.detail?.attempts || [];
    if (!a.length) return null;
    const sorted = [...a].sort((x, y) => Date.parse(x.closedAt) - Date.parse(y.closedAt));
    const best = sorted.reduce((m, x) => ((x.closingBidPence || 0) > (m.closingBidPence || 0) ? x : m), sorted[0]);
    return {
      count: a.length,
      since: monthYear(sorted[0].closedAt),
      best: best.closingBidPence || 0,
      bestAt: monthYear(best.closedAt),
      sold: a.filter((x) => (x.resultStatus || '').toLowerCase() === 'sold').length,
    };
  }

  public monthYear = monthYear;

  /** Closing bid at each earlier attempt, ending with the current bid. */
  private buildSpark(attempts: AuctionAttempt[], current: number | null): SparkModel | null {
    const sorted = [...attempts].sort((x, y) => Date.parse(x.closedAt) - Date.parse(y.closedAt));
    const pts = sorted.map((a) => a.closingBidPence || 0).concat(current ? [current] : []);
    if (pts.length < 2) return null;
    const W = 460;
    const H = 120;
    const P = 26;
    const top = Math.max(...pts) * 1.08 || 1;
    const x = (i: number) => P + (i * (W - P * 2)) / (pts.length - 1);
    const y = (v: number) => H - 18 - (v / top) * (H - 36);
    const step = niceStep(top / 2);
    const grid: { y: number; label: string }[] = [];
    for (let v = step; v < top; v += step) grid.push({ y: y(v), label: shortGbp(v) });
    return {
      w: W,
      h: H,
      p: P,
      path: pts.map((v, i) => (i ? 'L' : 'M') + x(i).toFixed(1) + ' ' + y(v).toFixed(1)).join(' '),
      dots: pts.map((v, i) => {
        const isNow = i === pts.length - 1 && !!current;
        return { x: x(i), y: y(v), r: isNow ? 4.5 : 2.6, fill: isNow ? '#1f9d57' : '#0e2b33' };
      }),
      grid: grid.slice(0, 3),
      firstLabel: monthYear(sorted[0].closedAt),
    };
  }

  private lockBody(): void {
    if (typeof document !== 'undefined') document.body.style.overflow = 'hidden';
  }
  private unlockBody(): void {
    if (typeof document !== 'undefined') document.body.style.overflow = '';
  }

  // ------------------------------------------------------- locked-in estimates
  // Each lot is valued ONCE by the PlateX valuation tool before it ends (hourly
  // job in platex-assistant) and scored against the all-in result. Admin-only.
  get showEstimates(): boolean {
    return Object.keys(this.estimates).length > 0;
  }

  private estHeaders(): HeadersInit | null {
    const url = (environment as any).assistantUrl as string;
    if (!url || !this.sessionService.isAdmin()) return null;
    return { Authorization: 'Bearer ' + this.sessionService.getToken() };
  }

  private loadEstimates(retried = false): void {
    const headers = this.estHeaders();
    if (!headers) return;
    const base = ((environment as any).assistantUrl as string).replace(/\/$/, '');
    fetch(base + '/auction-estimates', { headers })
      .then((r) => {
        // fetch() skips the token interceptor: on 401 refresh the session once and retry.
        if (r.status === 401 && !retried && this.sessionService.isLoggedIn()) {
          this.sessionService.RefreshToken().subscribe({
            next: () => this.zone.run(() => this.loadEstimates(true)),
            error: () => {},
          });
          return null;
        }
        return r.ok ? r.json() : null;
      })
      .then((j) => {
        if (!j || !j.estimates) return;
        this.estimates = j.estimates;
        this.cdr.markForCheck();
        this.loadScorecard(base);
      })
      .catch(() => {});
  }

  private loadScorecard(base: string): void {
    const headers = this.estHeaders();
    if (!headers) return;
    fetch(base + '/auction-estimates/scorecard', { headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!j) return;
        this.estScore = { overall: j.overall || { scored: 0 }, byOutcome: j.lots_by_outcome || {} };
        this.cdr.markForCheck();
      })
      .catch(() => {});
  }

  /** Results for one sale: collect every finished lot in it (all pages) so the scorecard covers that sale only. */
  private loadSaleResultKeys(): void {
    const sale = this.state.sale;
    if (!sale || this.state.tab !== 'results' || !this.estHeaders()) return;
    if (this.saleResultKeys?.sale === sale) return;
    this.saleKeysSub?.unsubscribe();
    const keys: string[] = [];
    const page = (n: number) => {
      this.saleKeysSub = this.service
        .getLots({ tab: 'results', house: 'all', sale, page: n, pageSize: 200 })
        .subscribe(
          (r) => {
            (r.items || []).forEach((d) => keys.push(d.house + ':' + d.id));
            if (n * 200 < (r.total || 0) && n < 50) return page(n + 1);
            this.saleResultKeys = { sale, keys };
            this.cdr.markForCheck();
          },
          () => {}
        );
    };
    page(1);
  }

  /** Scorecard for the panel: the filtered sale's own lots when a sale is set, otherwise everything. */
  get shownScore(): EstScore['overall'] | null {
    if (!this.estScore) return null;
    if (!this.state.sale) return this.estScore.overall;
    const keys = this.saleResultKeys;
    if (keys?.sale !== this.state.sale) return null;
    const memo = this.saleScoreMemo;
    if (memo && memo.keys === keys && memo.estimates === this.estimates) return memo.score;
    const score = scoreOf(keys.keys.map((k) => this.estimates[k]).filter((e) => !!e));
    this.saleScoreMemo = { keys, estimates: this.estimates, score };
    return score;
  }

  estFor(l: LotVm): LotEstimate | null {
    return this.estimates[l.dto.house + ':' + l.dto.id] || null;
  }

  estRange(e: LotEstimate): string {
    if (!e.est_low_pence || !e.est_high_pence) return e.est_mid_pence ? gbp(e.est_mid_pence) : '—';
    return gbpShort(e.est_low_pence) + '–' + gbpShort(e.est_high_pence);
  }

  /** Results view: how the locked-in estimate did against the all-in price. */
  estVerdict(e: LotEstimate): { text: string; cls: string } | null {
    if (e.outcome === 'sold' && e.error_pct != null) {
      const sign = e.error_pct > 0 ? '+' : '';
      return e.in_range
        ? { text: '✓ in range (' + sign + e.error_pct + '%)', cls: 'est-ok' }
        : { text: '✗ ' + sign + e.error_pct + '%', cls: 'est-miss' };
    }
    if (e.outcome === 'unsold' || e.outcome === 'no_confirmed_sale') return { text: 'unsold · not scored', cls: 'est-na' };
    if (e.outcome === 'awaiting_result') return { text: 'awaiting result', cls: 'est-na' };
    return null;
  }
}

/** Same measures as platex-assistant's scorecard: sold lots with an error only, 1 dp. */
function scoreOf(rows: LotEstimate[]): EstScore['overall'] {
  const sold = rows.filter((r) => r.outcome === 'sold' && r.error_pct != null);
  if (!sold.length) return { scored: 0 };
  const errs = sold.map((r) => Math.abs(r.error_pct as number)).sort((a, b) => a - b);
  const mid = errs.length >> 1;
  const median = errs.length % 2 ? errs[mid] : (errs[mid - 1] + errs[mid]) / 2;
  const pct = (n: number) => Math.round((1000 * n) / sold.length) / 10;
  return {
    scored: sold.length,
    in_range_pct: pct(sold.filter((r) => r.in_range).length),
    median_abs_error_pct: Math.round(median * 10) / 10,
    within_25pct: pct(errs.filter((e) => e <= 25).length),
  };
}

function niceStep(raw: number): number {
  if (raw <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const n = raw / mag;
  return (n < 1.5 ? 1 : n < 3.5 ? 2 : n < 7.5 ? 5 : 10) * mag;
}

function shortGbp(pence: number): string {
  const pounds = pence / 100;
  return pounds >= 1000 ? '£' + Math.round(pounds / 100) / 10 + 'k' : '£' + Math.round(pounds);

}

export interface LotEstimate {
  registration: string;
  engine: string;
  estimated_at: string;
  est_low_pence: number | null;
  est_mid_pence: number | null;
  est_high_pence: number | null;
  confidence: string;
  comp_count: number;
  outcome: string;
  all_in_pence?: number;
  in_range?: boolean;
  error_pct?: number;
}

export interface EstScore {
  overall: { scored: number; in_range_pct?: number; median_abs_error_pct?: number; within_25pct?: number };
  byOutcome: { [k: string]: number };
}

/** £56k / £950 / £1.2m for compact ranges. */
function gbpShort(pence: number): string {
  const p = pence / 100;
  if (p >= 1e6) return '£' + (p / 1e6).toFixed(p >= 1e7 ? 0 : 1).replace(/\.0$/, '') + 'm';
  if (p >= 1e4) return '£' + Math.round(p / 1000) + 'k';
  if (p >= 1e3) return '£' + (p / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  return '£' + Math.round(p);
}
